import React, { useState, useEffect, useMemo, useContext } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import ReactGA from "react-ga4";
import MenuBars from "../../assets/imgs/menu-bars.svg";
import Menu from "./Menu";
import Logo from "../../assets/imgs/JG-Símbolo_Branco.svg";
import "./Header.css";
import useTranslatedMenuItems from "./Btns-Content";
import MenuBtn from "./MenuBtn";
import LanguageIcon from "@mui/icons-material/Language";
import { LanguageContext } from "../../context/LanguageContext";

const trackEvent = (category, action, label) => {
  ReactGA.event({ category, action, label });
};

const LanguageSwitch = ({ language, onChange }) => (
  <button
    type="button"
    className="lang-switch-btn"
    aria-label="Language"
    onClick={() => onChange(language === "en" ? "pt" : "en")}
  >
    <LanguageIcon sx={{ fontSize: 18, mr: 0.75 }} />
    {language === "pt" ? "Português" : "English"}
  </button>
);

const Header = ({ scrollToComponent }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(() => (typeof window !== "undefined" ? window.scrollY > 50 : false));
  const { language, changeLanguage } = useContext(LanguageContext);

  const toggleSidebar = () => {
    setIsOpen((open) => !open);
  };

  useEffect(() => {
    if (!isOpen) {
      document.body.classList.remove("menu-open");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      return undefined;
    }

    const y = window.scrollY;
    document.body.classList.add("menu-open");
    document.body.style.position = "fixed";
    document.body.style.top = `-${y}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.classList.remove("menu-open");
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, y);
    };
  }, [isOpen]);

  const action = (address) => {
    if (address !== "Link") {
      scrollToComponent(address);
    } else {
      window.scrollTo(0, 0);
    }
  };

  const scrollUp = () => {
    window.scrollTo(0, 0);
  };

  const menuItems = useTranslatedMenuItems();

  const memoizedMenuItems = useMemo(
    () =>
      menuItems.map((menuItem, index) => (
        <div key={index}>
          <MenuBtn action={action} data={menuItem} scrollToComponent={scrollToComponent} />
        </div>
      )),
    [menuItems]
  );

  const main = process.env.REACT_APP_MAIN;

  useEffect(() => {
    let frame = 0;
    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (document.body.classList.contains("menu-open")) return;
        const next = window.scrollY > 50;
        setIsScrolled((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const handleLanguageChange = (newLanguage) => {
    changeLanguage(newLanguage);
    trackEvent("Preferences", "change", `Language - ${newLanguage}`);
  };

  const whatsappMsg = "Olá Jacques, vi seu perfil e gostaria de discutir um projeto de IA.";
  const whatsappUrl = `https://wa.me/5584994514529?text=${encodeURIComponent(whatsappMsg)}`;

  const textContent = {
    en: { contact: "Get in touch" },
    pt: { contact: "Contato" },
  };

  const { contact } = textContent[language];

  return (
    <div className={`main-header ${isScrolled ? "scrolled" : ""} header-wrapper`}>
      <div className="header-container page-width">
        <div className="header-img-div">
          <div className="logo-link header-img">
            <Link
              to={main}
              onClick={() => {
                scrollUp();
                trackEvent("Navigation", "click", "Logo");
              }}
            >
              <img className="logo-link header-img" src={Logo} alt="Jacques Gomes" />
            </Link>
          </div>
        </div>
        <nav>
          {memoizedMenuItems}
          <LanguageSwitch language={language} onChange={handleLanguageChange} />
          <a
            className="header-cta"
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackEvent("Contact", "click", "Header Contact Button")}
          >
            {contact}
          </a>
        </nav>

        <div className="menu-toggle" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <LanguageSwitch language={language} onChange={handleLanguageChange} />
          <button className="menu-toggle" onClick={toggleSidebar} aria-label="Toggle menu">
            <img src={MenuBars} alt="Menu Bars" />
          </button>
        </div>
      </div>

      {createPortal(
        <Menu
          scrollToComponent={scrollToComponent}
          toggleSidebar={toggleSidebar}
          isOpen={isOpen}
        />,
        document.body
      )}
    </div>
  );
};

export default Header;
